import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';

/**
 * A file ANNOUNCED before it exists: the agent attaches this while a design is being generated, so the
 * reply shows the files arriving, and replaces it with the real asset when that lands (the harness's
 * AttachFile, through the `attachments` mask of UpdateConversationMessage). It carries a name and a type
 * and NEITHER address, which is what identifies it — there is no flag for anything to disagree with.
 *
 * The swap reaches an open chat on the ASSISTANT_CHAT push that already delivers attachments to a
 * streamed bubble (`patchAttachmentsById`), so nothing here has to poll or refetch.
 *
 * Kept as its own module rather than shared from common-lib, and duplicated in the harness and both
 * phones, the way `file-record` is: it is two comparisons, and each surface expresses them in its own
 * language anyway.
 */
export function isPlaceholderAttachment(attachment: Pick<MessageAttachment, 'url' | 'vfsPath'>): boolean {
  return !attachment.url?.trim() && !attachment.vfsPath?.trim();
}

/** Beyond this, a picture that never arrived reads as stuck rather than merely slow — a real render lands
 *  within a couple of minutes even on a heavy edit, and nothing here can tell "still working" from "the
 *  agent gave up" any more precisely than that. */
export const STALE_PLACEHOLDER_MS = 10 * 60_000;

/** Whether a placeholder created at `createdAt` (the row's own timestamp — never a client guess) has been
 *  waiting long enough to treat as abandoned. */
export function isStalePlaceholder(createdAt: string | undefined, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  return Number.isFinite(created) && now - created > STALE_PLACEHOLDER_MS;
}
